"use client";
"use no memo";

import React, { useRef } from "react";
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, VirtualizerOptions } from "@tanstack/react-virtual";
import { cn } from "../../lib/utils";
import { AnyColumnDef, DataTableSortState } from "./data-table-types";
import { useDataTableSortHelpers } from "./data-table-internal-hooks";
import {
	AUTOSIZING_DEFAULT_COLUMN,
	getColumnWidth,
	getMetaWithDefaults,
} from "./data-table-utils";
import { Table } from "./table";
import { Skeleton } from "./skeleton";

type VirtualTableProps<
	TData extends { id: string },
	TSortField extends string = string,
> = {
	/** Row data for the table body. Each item must include a stable `id`. */
	data: TData[];
	/** TanStack Table column definitions used to render headers and cells. */
	columns: AnyColumnDef<TData>[];
	/** When true, renders loading skeleton rows instead of table data. */
	isLoading?: boolean;
	/** Number of loading skeleton rows to show while `isLoading` is true. */
	loadingRowCount?: number;
	/** Optional base test id used to derive header/row/cell test ids. */
	"data-testid"?: string;
	/** Empty-state content shown when not loading and no rows exist. */
	emptyState?: React.ReactNode;
	/** Maximum body height before scrolling; header height is not included. */
	maxHeight?: number | string;
	/** Controlled sort state used by sortable column headers. */
	sort?: DataTableSortState<TSortField>;
	/** Called when sort state changes from a sortable header interaction. */
	onSortChange?: (value: DataTableSortState<TSortField>) => void;
	/** Partial overrides for TanStack Virtualizer options. */
	options?: Partial<VirtualizerOptions<HTMLDivElement, Element>>;
};

const ESTIMATED_ROW_HEIGHT = 64;

export function VirtualTable<
	TData extends { id: string },
	TSortField extends string = string,
>({
	data,
	columns,
	sort,
	onSortChange,
	isLoading = false,
	loadingRowCount = 5,
	"data-testid": testId,
	emptyState = null,
	maxHeight = "500px",
	options,
}: VirtualTableProps<TData, TSortField>) {
	// eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table returns a non-memoizable instance; see "use no memo" on this module
	const table = useReactTable({
		data,
		columns,
		defaultColumn: AUTOSIZING_DEFAULT_COLUMN,
		getCoreRowModel: getCoreRowModel(),
		getRowId: (row) => row.id,
	});

	const rowModel = table.getRowModel();
	const headerGroups = table.getHeaderGroups();
	const numCols = Math.max(table.getVisibleFlatColumns().length, 1);
	const makeTestId = (id: string) => (testId ? `${testId}-${id}` : null);

	const { getIsColumnSortable, getIsColumnSorted, getSortHandler } =
		useDataTableSortHelpers({
			state: sort,
			onChange: onSortChange,
		});

	const parentRef = useRef<HTMLDivElement>(null);

	const virtualizer = useVirtualizer({
		count: data.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => ESTIMATED_ROW_HEIGHT,
		...options,
	});

	return (
		<div className="border border-muted rounded-md overflow-x-auto">
			<div
				ref={parentRef}
				style={{
					minHeight: 0,
					maxHeight,
					overflow: "auto",
				}}
			>
				<Table data-testid={testId}>
					{headerGroups.map((headerGroup) => (
						<Table.Header
							data-testid={makeTestId("header")}
							key={headerGroup.id}
						>
							{headerGroup.headers.map((header) => {
								const { column } = header;
								const meta = getMetaWithDefaults(column);

								return (
									<Table.Column
										key={header.id}
										data-testid={makeTestId(`header-${header.id}`)}
										style={{ width: getColumnWidth(column) }}
										className={cn(
											"overflow-hidden",
											meta.align === "left" && "text-left",
											meta.align === "center" && "text-center",
											meta.align === "right" && "text-right",
										)}
										sortable={getIsColumnSortable(column)}
										sorted={getIsColumnSorted(column)}
										onClick={getSortHandler(column)}
										sortDirection={sort?.direction}
									>
										{header.isPlaceholder
											? null
											: flexRender(
													column.columnDef.header,
													header.getContext(),
												)}
									</Table.Column>
								);
							})}
						</Table.Header>
					))}
					{!isLoading && rowModel.rows.length !== 0 && (
						<Table.Body style={{ height: `${virtualizer.getTotalSize()}px` }}>
							{virtualizer.getVirtualItems().map((virtualRow, index) => {
								const row = rowModel.rows[virtualRow.index];
								return (
									// Table.Body uses block layout (not the native table algorithm), so rows stay in flow.
									// We size each row with `virtualRow.size` and offset with `(virtualRow.start - index * virtualRow.size)`
									// to support variable heights while preserving semantic table markup.
									<Table.Row
										key={row.id}
										style={{
											height: `${virtualRow.size}px`,
											transform: `translateY(${virtualRow.start - index * virtualRow.size}px)`,
										}}
										data-testid={makeTestId(`row-${row.original.id}`)}
									>
										{row.getVisibleCells().map((cell) => {
											const { align, truncate } = getMetaWithDefaults(
												cell.column,
											);
											const columnWidth = cell.column.getSize();

											return (
												<Table.Cell
													key={cell.id}
													data-testid={makeTestId(`cell-${cell.column.id}`)}
													style={{
														width:
															columnWidth !== 150 ? columnWidth : undefined,
													}}
													className={cn(
														"overflow-hidden",
														truncate && "truncate",
														align === "left" && "text-left",
														align === "center" && "text-center",
														align === "right" && "text-right",
													)}
												>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</Table.Cell>
											);
										})}
									</Table.Row>
								);
							})}
						</Table.Body>
					)}
					{!isLoading && rowModel.rows.length === 0 && (
						<Table.Body>
							<Table.Row>
								<Table.Cell colSpan={numCols}>
									<div className="flex min-h-[200px] items-center justify-center">
										{emptyState}
									</div>
								</Table.Cell>
							</Table.Row>
						</Table.Body>
					)}
					{isLoading ? (
						<Table.Body>
							{Array.from({ length: loadingRowCount }).map((_, index) => (
								<Table.Row key={`loading-${index}`}>
									{table.getVisibleFlatColumns().map((column) => {
										const columnWidth = column.getSize();
										return (
											<Table.Cell
												key={column.id}
												style={{
													width: columnWidth !== 150 ? columnWidth : undefined,
												}}
											>
												<Skeleton className="my-[5px] flex h-[13px] items-center rounded-md" />
											</Table.Cell>
										);
									})}
								</Table.Row>
							))}
						</Table.Body>
					) : null}
				</Table>
			</div>
		</div>
	);
}
